import React, { useEffect, useRef } from "react";
import MaterialIcon from "../MaterialIcon";
import ChatAvatar from "./ChatAvatar";
import teamsLogo from "../../Icons/Teams.png";
import "./chat-pro.css";

// Chat header of the mobile Pro chat (v2/pro/ChatHeaderPro.js), adapted to the web. Three states:
//   normal  ‹ back · 40 avatar · name + subline (online / members · online / typing) ·
//           video · call · search (no overflow menu: the name opens the chat info)
//   search  ‹ close · "Search in this chat" (Enter starts) · "Searching…" · "x/y" (ranked, opens on the best) ·
//           ▲ next · ▼ previous (usable while the search still runs)
//   select  × · "N selected" · copy · forward · delete (red, only when every selected message is deletable)
// Every label comes from the owner; this component only decides the look. `bridgeLabel` ("Kjuup +
// Teams") marks a Kjuup group with a Microsoft Teams copy: Teams logo + label after the subline.

function IconButton({ icon, label, onClick, disabled, danger, active, size = 24, className = "", fill = 0 }) {
  return (
    <button
      type="button"
      className={`kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kjc-header__icon${danger ? " is-danger" : ""}${active ? " is-active" : ""} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <MaterialIcon icon={icon} size={size} weight={300} fill={fill} />
    </button>
  );
}

function SearchBar({ search, labels }) {
  const inputRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => clearTimeout(t);
  }, []);
  const total = Number(search.total) || 0;
  const at = Math.min(Math.max(Number(search.index) || 0, 0), Math.max(total - 1, 0));
  const term = String(search.value || "").trim();
  // The field is a draft: Enter makes it the active query (`search.query`), so typing alone
  // neither searches nor loads history. Without an owner-side query the field searches live.
  const query = search.onSubmit ? String(search.query || "").trim() : term;
  const dirty = term !== query;
  const busy = !!search.busy && query.length >= 2;
  const jumping = busy && !!search.jumping;
  const position = total > 0 ? `${at + 1}/${total}` : "";
  // One status at a time: unsent term → "Enter to search"; jumping → "Loading result 1/4…";
  // index still growing → spinner beside the (growing) "x/y", or "Searching…" while nothing is
  // found yet.
  let status = null;
  if (dirty) {
    if (term.length >= 2 && labels.pressEnter) status = { text: labels.pressEnter, spin: false };
  } else if (jumping && labels.loadingResult) {
    status = { text: labels.loadingResult.replace("{n}", position).replace(/\s{2,}/g, " "), spin: true };
  } else if (busy) {
    status = { text: total ? "" : labels.searching, spin: true, title: labels.searching };
  }
  // Results are ranked (best first): the search opens on 1/y.
  const count = dirty || jumping ? "" : position || (query.length >= 2 && !busy ? labels.noResults || "0" : "");
  // ▲ / ▼ work while the index still grows and while a jump loads pages (a new step takes over);
  // only an unsent term locks them, since they would walk the previous query's results.
  const navLocked = dirty;
  return (
    <div className="kjc-header__row kjc-header__row--search">
      <IconButton icon="keyboard_arrow_left" size={28} label={labels.closeSearch} onClick={search.onClose} />
      <label className="kjc-header__search">
        <MaterialIcon icon="search" size={20} weight={300} className="kjc-header__search-icon" />
        <input
          ref={inputRef}
          type="text"
          value={search.value || ""}
          placeholder={labels.searchPlaceholder}
          onChange={(e) => search.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (dirty) search.onSubmit(term);
              else if (e.shiftKey) search.onNewer();
              else search.onOlder();
            } else if (e.key === "Escape") {
              e.preventDefault();
              search.onClose();
            }
          }}
          aria-label={labels.searchPlaceholder}
        />
        {search.value ? (
          <button type="button" className="kjc-header__search-clear" onClick={search.onClear} aria-label={labels.clear} title={labels.clear}>
            <MaterialIcon icon="cancel" size={18} fill={1} weight={400} />
          </button>
        ) : null}
      </label>
      {status ? (
        <span
          className={`kjc-header__hint${status.spin ? "" : " kjc-header__hint--static"}`}
          role="status"
          title={status.title}
          aria-label={status.text ? undefined : status.title}
        >
          {status.spin ? <span className="kjc-header__spinner" aria-hidden="true" /> : null}
          {status.text}
        </span>
      ) : null}
      {count ? <span className="kjc-header__count" aria-live="polite">{count}</span> : null}
      <IconButton icon="keyboard_arrow_up" label={labels.prevMatch} onClick={search.onOlder} disabled={!total || navLocked || search.olderDisabled} />
      <IconButton icon="keyboard_arrow_down" label={labels.nextMatch} onClick={search.onNewer} disabled={!total || navLocked || search.newerDisabled} />
    </div>
  );
}

export default function ChatHeaderPro({
  internal = false,
  showBack = false,
  onBack,
  avatar,
  title,
  subline,
  sublineAccent = false,
  bridgeLabel = null,
  loading = false,
  onOpenInfo,
  calls,
  callSlot,
  onSearch,
  search,
  selection,
  labels = {},
}) {
  const rootClass = `kjc-header${internal ? " kjc-internal" : ""}`;

  if (selection) {
    const n = Number(selection.count) || 0;
    return (
      <div className={`${rootClass} kjc-header--select`}>
        <div className="kjc-header__row">
          <IconButton icon="close" label={labels.cancel} onClick={selection.onCancel} />
          <div className="kjc-header__select-title" aria-live="polite">{`${n} ${labels.selected}`}</div>
          {selection.onCopy ? <IconButton icon="content_copy" label={labels.copy} onClick={selection.onCopy} disabled={n === 0} /> : null}
          {selection.onForward ? <IconButton icon="forward" label={labels.forward} onClick={selection.onForward} disabled={n === 0} /> : null}
          {selection.onDelete && selection.canDelete ? (
            <IconButton icon="delete" label={labels.delete} onClick={selection.onDelete} disabled={n === 0} danger />
          ) : null}
        </div>
      </div>
    );
  }

  if (search) {
    return (
      <div className={`${rootClass} kjc-header--search`}>
        <SearchBar search={search} labels={labels} />
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div className="kjc-header__row">
        {showBack ? <IconButton icon="keyboard_arrow_left" size={28} label={labels.back} onClick={onBack} className="kjc-header__back" /> : null}
        <div
          className={`kjc-header__who${onOpenInfo ? " is-clickable" : ""}${showBack ? "" : " no-back"}`}
          role={onOpenInfo ? "button" : undefined}
          tabIndex={onOpenInfo ? 0 : undefined}
          onClick={onOpenInfo}
          onKeyDown={(e) => {
            if (onOpenInfo && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onOpenInfo();
            }
          }}
          title={labels.openInfo}
        >
          {loading ? (
            <>
              <span className="kjc-skeleton kjc-skeleton--avatar" />
              <span className="kjc-header__text">
                <span className="kjc-skeleton kjc-skeleton--line" />
                <span className="kjc-skeleton kjc-skeleton--line kjc-skeleton--short" />
              </span>
            </>
          ) : (
            <>
              {avatar ? <ChatAvatar size={40} {...avatar} /> : null}
              <span className="kjc-header__text">
                <span className="kjc-header__title">{title}</span>
                {subline || bridgeLabel ? (
                  <span className={`kjc-header__sub${sublineAccent ? " is-accent" : ""}`}>
                    {subline}
                    {subline && bridgeLabel ? " · " : null}
                    {bridgeLabel ? (
                      <span className="kjc-header__bridge">
                        <img src={teamsLogo} alt="" />
                        {bridgeLabel}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </span>
            </>
          )}
        </div>
        {callSlot || null}
        {!callSlot && calls && calls.visible ? (
          <>
            <IconButton icon="videocam" label={labels.videoCall} onClick={calls.onVideo} active={calls.activeKind === "video"} />
            <IconButton icon="call" label={labels.audioCall} onClick={calls.onAudio} active={calls.activeKind === "audio"} />
          </>
        ) : null}
        {onSearch ? <IconButton icon="search" label={labels.searchInChat} onClick={onSearch} /> : null}
      </div>
    </div>
  );
}
